import { supabase } from "@/lovable-demo/runtime/client";
import { getSignedInActor } from "./audit";

/**
 * AI governance metadata.
 *
 * Every AI draft is logged as advisory only, with the model and prompt version,
 * and explicitly flagged as requiring a human decision. An AI interaction is
 * never an authorised decision; the authorised decision remains the human
 * verdict stored on the record.
 */

export interface AiInteractionInput {
  recordId: string;
  recordCode?: string;
  stageId?: string;
  recommendationId?: string;
  purpose?: string;
  model: string;
  promptVersion?: string;
  inputSummary?: string;
  outputSummary?: string;
  outcome?: "drafted" | "failed" | "rate-limited" | "not-configured";
  error?: string;
}

export async function logAiInteraction(input: AiInteractionInput): Promise<void> {
  try {
    const actor = await getSignedInActor();
    await supabase.from("ai_interactions").insert({
      record_id: input.recordId,
      record_code: input.recordCode ?? "",
      stage_id: input.stageId ?? "",
      recommendation_id: input.recommendationId ?? "",
      purpose: input.purpose ?? "recommendation-brief",
      provider: "synthetic-local",
      model: "deterministic-demo",
      prompt_version: input.promptVersion ?? "v1",
      advisory_only: true,
      human_decision_required: true,
      outcome: input.outcome ?? "drafted",
      input_summary: (input.inputSummary ?? "").slice(0, 500),
      output_summary: (input.outputSummary ?? "").slice(0, 1000),
      error: input.error ?? "",
      requested_by_user_id: actor.id,
    });
  } catch {
    /* non-blocking */
  }
}

export async function listAiInteractions(recordId: string) {
  const { data } = await supabase
    .from("ai_interactions")
    .select("*")
    .eq("record_id", recordId)
    .order("created_at", { ascending: false });
  return data ?? [];
}
